import { useState, useEffect, useCallback, useRef } from "react";
import axios from "axios";
import { X, Truck, Loader2, AlertCircle, CheckCircle2, Search, ExternalLink, Wallet, Upload } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function AwbGeneratorModal({ order, open, onClose, onCreated }) {
  const c = order?.customer || {};
  const details = order?.details || {};
  // Locker id source priority — covers all marketplaces:
  //   • eMAG → details.courier_external_office_id (numeric Sameday id)
  //   • Gomag → order.locker_id (extracted from raw_gomag.delivery.lockerId
  //     during sync — see gomag_client.py)
  //   • Legacy / older shapes kept as fallback
  const lockerFromOrder =
    details.courier_external_office_id ||
    order?.locker_id ||
    c.shipping_locker_id ||
    c.locker_id ||
    details.locker_id;
  const lockerNameFromOrder = order?.locker_name || details.locker_name;
  const deliveryMode = (order?.delivery_mode || "").toLowerCase();
  // Substring match — Gomag returns "Livrare la pachetomat EasyBox Sameday",
  // so we can't rely on exact equality. Any of these tokens means locker.
  const lockerKeywords = ["pickup", "easybox", "locker", "pachetomat"];
  const isLockerOrder = !!lockerFromOrder || lockerKeywords.some((k) => deliveryMode.includes(k));

  const isEmagOrder = (order?._integration_key || "").startsWith("emag_");

  const [awbSource, setAwbSource] = useState(isEmagOrder ? "emag_sameday_locker" : "bragus");
  const [shippingType, setShippingType] = useState(isLockerOrder ? "locker" : "home");

  // Reset defaults whenever the modal is (re)opened on a different order.
  useEffect(() => {
    if (!open || !order) return;
    const initialSource = isEmagOrder ? (isLockerOrder ? "emag_sameday_locker" : "emag_sameday_home") : "bragus";
    setAwbSource(initialSource);
    setShippingType(isLockerOrder ? "locker" : "home");
    setLockerId(lockerFromOrder || "");
    setResult(null); setError("");
  }, [open, order?.id]);  // eslint-disable-line
  const [weight, setWeight] = useState(1);
  const [parcels, setParcels] = useState(1);
  const [observation, setObservation] = useState("");
  const [insuredValue, setInsuredValue] = useState(0);
  const [lockerId, setLockerId] = useState(lockerFromOrder || "");
  const [lockerSearch, setLockerSearch] = useState(c.shipping_city || c.billing_city || "");
  const [lockers, setLockers] = useState([]);
  const [lockerLoading, setLockerLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);
  const [result, setResult] = useState(null);
  const [error, setError] = useState("");
  const [cod, setCod] = useState(null);
  const [codLoading, setCodLoading] = useState(false);
  // Gomag "Plata online" but unpaid — operator must decide whether to charge
  // the order value as ramburs on the AWB. `null` = not yet chosen (blocks
  // submit + shows the red warning dialog). See user request 2026-07-23.
  const [rambursChoice, setRambursChoice] = useState(null); // null | "yes" | "no"

  const [codOverride, setCodOverride] = useState(null); // null = auto | number = user-set
  const [codOverrideDraft, setCodOverrideDraft] = useState("");
  const [showCodEditor, setShowCodEditor] = useState(false);

  const isGomagOrder = (order?._integration_key || "").startsWith("gomag");

  const fetchCod = useCallback(async (oid, intKey) => {
    setCodLoading(true);
    try {
      const r = await axios.get(`${API}/orders/${oid}/cod-preview`, { params: { integration_key: intKey } });
      setCod(r.data);
    } catch {
      setCod(null);
    } finally {
      setCodLoading(false);
    }
  }, []);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (!open || !order) return;
    fetchCod(order.id, order._integration_key);
    setRambursChoice(null);
    setCodOverride(null);
    setCodOverrideDraft("");
    setShowCodEditor(false);
  }, [open, order?.id, fetchCod]);

  const country = (c.shipping_country || c.billing_country || "RO").toUpperCase();

  const searchLockers = useCallback(async () => {
    if (shippingType !== "locker") return;
    setLockerLoading(true);
    try {
      const r = await axios.get(`${API}/sameday/lockers`, { params: { country, city: lockerSearch, name: lockerSearch } });
      const all = r.data?.lockers || [];
      const filtered = lockerSearch
        ? all.filter((l) => (l.city || "").toLowerCase().includes(lockerSearch.toLowerCase()) ||
                            (l.name || "").toLowerCase().includes(lockerSearch.toLowerCase()))
        : all;
      setLockers(filtered.slice(0, 50));
    } catch (e) {
      setError(e.response?.data?.detail || e.message);
    } finally { setLockerLoading(false); }
  }, [shippingType, country, lockerSearch]);

  if (!open || !order) return null;

  const submit = async () => {
    setSubmitting(true); setError(""); setResult(null);
    try {
      const body = {
        shipping_type: shippingType,
        weight: Number(weight) || 1,
        parcels: Number(parcels) || 1,
        observation,
        insured_value: Number(insuredValue) || 0,
      };
      if (shippingType === "locker" && lockerId) body.locker_id = String(lockerId);
      // Gomag online-unpaid: forward the operator's ramburs choice to the
      // backend. `yes` → charge suggested_cod as ramburs; `no` → explicit 0.
      if (cod?.is_online_unpaid && rambursChoice) {
        body.cod_override = rambursChoice === "yes" ? Number(cod.suggested_cod || 0) : 0;
      }
      // Gomag COD orders: operator can override the ramburs amount (e.g.
      // client paid via bank transfer for a COD order — send AWB with 0).
      if (isGomagOrder && cod?.is_cod && codOverride !== null) {
        body.cod_override = Number(codOverride);
      }
      // Map UI awbSource → backend params
      const sourceMap = {
        emag_sameday_locker: { awb_source: "emag", awb_courier: "sameday_locker" },
        emag_fan_locker:     { awb_source: "emag", awb_courier: "fan_locker" },
        emag_sameday_home:   { awb_source: "emag", awb_courier: "sameday_home" },
        bragus:              { awb_source: "bragus" },
      };
      Object.assign(body, sourceMap[awbSource] || { awb_source: awbSource });
      const r = await axios.post(`${API}/orders/${order.id}/awb`, body);
      setResult(r.data);
      onCreated?.(r.data);
    } catch (e) {
      setError(e.response?.data?.detail || e.message);
    } finally { setSubmitting(false); }
  };

  // Upload PDF AWB manual — comportament identic cu generarea nativă:
  // push în Shopify (fulfill + tracking), WhatsApp cu AWB-ul, finalizare comandă.
  const handleUpload = async (file) => {
    if (!file) return;
    if (file.type !== "application/pdf") { setError("Doar fișiere PDF."); return; }
    const number = (window.prompt("Număr AWB (tracking):", order?.awb_number || "") || "").trim();
    if (!number) { setError("Numărul AWB este obligatoriu."); return; }
    setUploading(true); setError(""); setResult(null);
    try {
      const integ_key = order._integration_key || order.integration_key;
      const fd = new FormData();
      fd.append("file", file);
      fd.append("number", number);
      if (integ_key) fd.append("integration_key", integ_key);
      const r = await axios.post(`${API}/orders/${order.id}/awb/upload`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setResult({ awb_number: r.data?.awb_number, url: r.data?.awb_url, emag_push: "skipped_manual" });
      onCreated?.(r.data);
    } catch (e) {
      setError(e.response?.data?.detail || e.message);
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };


  const codAmount = cod?.is_online_unpaid
    ? (rambursChoice === "yes" ? Number(cod.suggested_cod || 0) : 0)
    : (isGomagOrder && cod?.is_cod && codOverride !== null ? Number(codOverride) : (cod?.total ?? 0));

  return (
    <div data-testid="awb-generator-modal" className="fixed inset-0 z-[60] flex items-end justify-center bg-black/70 backdrop-blur-sm sm:items-center sm:p-4">
      <div className="flex max-h-[95vh] w-full max-w-2xl flex-col overflow-hidden rounded-t-2xl border border-zinc-800 bg-zinc-950 shadow-2xl sm:rounded-2xl">
        <div className="flex items-center justify-between border-b border-zinc-800 bg-zinc-900/60 px-4 py-3">
          <div className="flex items-center gap-2 text-sm">
            <Truck className="h-4 w-4 text-sky-300" />
            <span className="font-semibold text-zinc-100">Generare AWB Sameday</span>
            <span className="rounded bg-orange-500/10 px-1.5 py-0.5 font-mono text-[10px] text-orange-200">#{order.id}</span>
            <span className="font-mono text-[10px] text-zinc-500">{country}</span>
          </div>
          <button data-testid="awb-modal-close" onClick={onClose} className="rounded-md p-1.5 text-zinc-500 hover:bg-zinc-800 hover:text-zinc-200"><X className="h-4 w-4" /></button>
        </div>

        <div className="flex-1 space-y-3 overflow-y-auto p-4">
          {error && <div data-testid="awb-error" className="flex items-start gap-2 rounded-lg border border-rose-500/40 bg-rose-500/10 px-3 py-2 text-xs text-rose-200"><AlertCircle className="mt-0.5 h-3.5 w-3.5 flex-shrink-0" /><span>{error}</span></div>}

          {!result && (
            <>
              {/* COD (ramburs) breakdown — single source of truth for the
                  amount the courier will collect. Only shown for ramburs
                  orders; online-paid orders skip this card. */}
              {cod?.is_cod && (
                <div data-testid="awb-cod-preview" className="rounded-lg border border-amber-500/30 bg-amber-500/5 p-3">
                  <div className="mb-2 flex items-center gap-2">
                    <Wallet className="h-3.5 w-3.5 text-amber-300" />
                    <span className="text-[10px] font-medium uppercase tracking-wider text-amber-200">Ramburs (COD)</span>
                    <span className="ml-auto rounded-full bg-amber-500/15 px-2 py-0.5 font-mono text-[10px] text-amber-200">{cod.source_field}</span>
                  </div>
                  <table className="w-full text-xs">
                    <tbody>
                      {(cod.lines || []).map((l, i) => (
                        <tr key={i} className="border-b border-amber-500/10 last:border-b-0">
                          <td className="py-1 pr-2 text-zinc-300" title={l.info || ""}>{l.label}{l.info && <span className="ml-1 text-[10px] text-zinc-500">ⓘ</span>}</td>
                          <td className={`py-1 text-right font-mono ${l.value < 0 ? "text-emerald-300" : "text-zinc-200"}`}>
                            {Number(l.value).toFixed(2)} {cod.currency}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                    <tfoot>
                      <tr className="border-t-2 border-amber-500/40">
                        <td className="py-1.5 pt-2 font-semibold text-amber-100">Total de încasat</td>
                        <td data-testid="awb-cod-total" className="py-1.5 pt-2 text-right font-mono text-base font-bold text-amber-200">
                          {Number(codAmount).toFixed(2)} {cod.currency}
                          {isGomagOrder && codOverride !== null && (
                            <span className="ml-1 rounded-full bg-sky-500/20 px-1.5 py-0 text-[9px] font-semibold uppercase text-sky-200">manual</span>
                          )}
                        </td>
                      </tr>
                    </tfoot>
                  </table>

                  {/* Gomag-only: allow operator to override the ramburs amount.
                      Use case: COD order, but client sent the money via bank
                      transfer separately — AWB should carry 0 ramburs. */}
                  {isGomagOrder && (
                    <div className="mt-2 border-t border-amber-500/20 pt-2">
                      {!showCodEditor ? (
                        <div className="flex flex-wrap items-center gap-2">
                          <button
                            type="button"
                            data-testid="awb-cod-override-open"
                            onClick={() => {
                              setShowCodEditor(true);
                              setCodOverrideDraft(codOverride !== null ? String(codOverride) : String(cod.total || 0));
                            }}
                            className="rounded-md border border-sky-500/40 bg-sky-500/10 px-2 py-1 text-[10px] font-medium text-sky-200 hover:bg-sky-500/20"
                          >
                            Modifică ramburs
                          </button>
                          {codOverride !== null && (
                            <button
                              type="button"
                              data-testid="awb-cod-override-reset"
                              onClick={() => { setCodOverride(null); setCodOverrideDraft(""); }}
                              className="rounded-md border border-zinc-700 bg-zinc-800 px-2 py-1 text-[10px] text-zinc-300 hover:bg-zinc-700"
                            >
                              Resetează la valoarea automată
                            </button>
                          )}
                          <span className="text-[10px] text-amber-300/80">
                            {codOverride === null
                              ? "Trimit AWB cu suma calculată automat."
                              : `Trimit AWB cu suma manuală: ${Number(codOverride).toFixed(2)} ${cod.currency}.`}
                          </span>
                        </div>
                      ) : (
                        <div className="space-y-1.5">
                          <div className="text-[10px] text-amber-200">
                            Suma pe care o va încasa curierul (ex: <span className="font-mono">0</span> dacă clientul a plătit deja prin transfer bancar).
                          </div>
                          <div className="flex items-center gap-1.5">
                            <input
                              data-testid="awb-cod-override-input"
                              type="number"
                              step="0.01"
                              min="0"
                              value={codOverrideDraft}
                              onChange={(e) => setCodOverrideDraft(e.target.value)}
                              className="w-32 rounded-md border border-amber-500/40 bg-zinc-900 px-2 py-1 text-xs text-zinc-100 outline-none focus:border-sky-500"
                              placeholder="0.00"
                            />
                            <span className="text-[10px] text-zinc-400">{cod.currency}</span>
                            <button
                              type="button"
                              data-testid="awb-cod-override-set-zero"
                              onClick={() => setCodOverrideDraft("0")}
                              className="rounded-md border border-zinc-700 bg-zinc-800 px-2 py-1 text-[10px] text-zinc-300 hover:bg-zinc-700"
                            >
                              Zero
                            </button>
                            <button
                              type="button"
                              data-testid="awb-cod-override-apply"
                              onClick={() => {
                                const n = Number(codOverrideDraft);
                                if (isNaN(n) || n < 0) return;
                                setCodOverride(Math.round(n * 100) / 100);
                                setShowCodEditor(false);
                              }}
                              className="rounded-md bg-sky-500 px-2 py-1 text-[10px] font-semibold text-zinc-950 hover:bg-sky-400"
                            >
                              Aplică
                            </button>
                            <button
                              type="button"
                              data-testid="awb-cod-override-cancel"
                              onClick={() => { setShowCodEditor(false); setCodOverrideDraft(""); }}
                              className="rounded-md border border-zinc-700 bg-zinc-800 px-2 py-1 text-[10px] text-zinc-300 hover:bg-zinc-700"
                            >
                              Renunță
                            </button>
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )}
              {codLoading && (
                <div className="rounded-lg border border-zinc-800 bg-zinc-900/40 px-3 py-2 text-xs text-zinc-500">
                  <Loader2 className="mr-1.5 inline h-3 w-3 animate-spin" /> Calculez ramburs...
                </div>
              )}
              {cod && !cod.is_cod && !cod.is_online_unpaid && (
                <div data-testid="awb-no-cod" className="rounded-lg border border-emerald-500/30 bg-emerald-500/5 px-3 py-2 text-xs text-emerald-200">
                  <CheckCircle2 className="mr-1.5 inline h-3 w-3" /> Plată online — fără ramburs de încasat.
                </div>
              )}
              {cod?.is_online_unpaid && (
                <div data-testid="awb-online-unpaid-warning" className="rounded-lg border-2 border-rose-500/60 bg-rose-500/10 p-3 text-sm text-rose-100">
                  <div className="flex items-start gap-2">
                    <AlertCircle className="mt-0.5 h-4 w-4 flex-shrink-0 text-rose-300" />
                    <div className="flex-1">
                      <p className="font-bold uppercase tracking-wide text-rose-200">Atenție: ONLINE — NEPLĂTITĂ</p>
                      <p className="mt-1 text-xs text-rose-100/90">
                        Comanda a fost setată cu <span className="font-mono">{cod.payment_mode_label}</span> dar plata nu a fost încă efectuată.
                      </p>
                      <p className="mt-2 text-xs font-semibold text-rose-100">
                        Adăugăm valoarea comenzii ({Number(cod.suggested_cod || 0).toFixed(2)} {cod.currency}) ca ramburs pe AWB?
                      </p>
                      {!rambursChoice ? (
                        <div className="mt-3 flex flex-wrap gap-2">
                          <button
                            data-testid="awb-ramburs-yes"
                            onClick={() => setRambursChoice("yes")}
                            className="inline-flex items-center gap-1 rounded-md bg-emerald-500 px-3 py-1.5 text-xs font-semibold text-zinc-950 hover:bg-emerald-400"
                          >
                            DA — adaugă {Number(cod.suggested_cod || 0).toFixed(2)} {cod.currency} ca ramburs
                          </button>
                          <button
                            data-testid="awb-ramburs-no"
                            onClick={() => setRambursChoice("no")}
                            className="inline-flex items-center gap-1 rounded-md border border-zinc-700 bg-zinc-900 px-3 py-1.5 text-xs font-semibold text-zinc-200 hover:border-zinc-600"
                          >
                            NU — ramburs 0
                          </button>
                          <button
                            data-testid="awb-ramburs-cancel"
                            onClick={onClose}
                            className="inline-flex items-center gap-1 rounded-md border border-rose-500/40 bg-rose-500/10 px-3 py-1.5 text-xs font-semibold text-rose-200 hover:bg-rose-500/20"
                          >
                            Anulează
                          </button>
                        </div>
                      ) : (
                        <div className="mt-3 flex items-center justify-between rounded-md border border-rose-500/40 bg-rose-500/5 px-3 py-2 text-xs">
                          <span className="text-rose-100">
                            {rambursChoice === "yes"
                              ? <>✅ Ramburs setat la <span className="font-mono font-bold">{Number(cod.suggested_cod || 0).toFixed(2)} {cod.currency}</span></>
                              : <>✅ Ramburs setat la <span className="font-mono font-bold">0.00 {cod.currency}</span></>}
                          </span>
                          <button
                            data-testid="awb-ramburs-change"
                            onClick={() => setRambursChoice(null)}
                            className="text-[10px] text-rose-200 underline hover:text-rose-100"
                          >
                            schimbă
                          </button>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              )}
              <div>
                <label className="block text-[10px] font-medium uppercase tracking-wider text-zinc-500">Sursă AWB</label>
                <div className="mt-1 grid grid-cols-1 gap-1.5 sm:grid-cols-2">
                  <button
                    data-testid="awb-source-emag-sameday-locker"
                    onClick={() => { setAwbSource("emag_sameday_locker"); setShippingType("locker"); }}
                    disabled={!isEmagOrder}
                    className={`rounded-md border px-3 py-2 text-left text-xs font-medium transition ${awbSource === "emag_sameday_locker" ? "border-amber-500/50 bg-amber-500/15 text-amber-200" : "border-zinc-800 bg-zinc-900 text-zinc-400 hover:border-zinc-700"} disabled:cursor-not-allowed disabled:opacity-40`}
                  >
                    eMAG Sameday EasyBox <span className="block text-[10px] opacity-70">Prefix 4EMG · apare în Sameday eAWB</span>
                  </button>
                  <button
                    data-testid="awb-source-emag-fan-locker"
                    onClick={() => { setAwbSource("emag_fan_locker"); setShippingType("locker"); }}
                    disabled={!isEmagOrder}
                    className={`rounded-md border px-3 py-2 text-left text-xs font-medium transition ${awbSource === "emag_fan_locker" ? "border-violet-500/50 bg-violet-500/15 text-violet-200" : "border-zinc-800 bg-zinc-900 text-zinc-400 hover:border-zinc-700"} disabled:cursor-not-allowed disabled:opacity-40`}
                  >
                    eMAG FAN Locker FANBox <span className="block text-[10px] opacity-70">Prefix 7000 · apare în FAN Courier</span>
                  </button>
                  <button
                    data-testid="awb-source-emag-sameday-home"
                    onClick={() => { setAwbSource("emag_sameday_home"); setShippingType("home"); }}
                    disabled={!isEmagOrder}
                    className={`rounded-md border px-3 py-2 text-left text-xs font-medium transition ${awbSource === "emag_sameday_home" ? "border-emerald-500/50 bg-emerald-500/15 text-emerald-200" : "border-zinc-800 bg-zinc-900 text-zinc-400 hover:border-zinc-700"} disabled:cursor-not-allowed disabled:opacity-40`}
                  >
                    eMAG Sameday Domiciliu <span className="block text-[10px] opacity-70">Sameday Regular prin eMAG</span>
                  </button>
                  <button
                    data-testid="awb-source-bragus"
                    onClick={() => setAwbSource("bragus")}
                    className={`rounded-md border px-3 py-2 text-left text-xs font-medium transition ${awbSource === "bragus" ? "border-sky-500/50 bg-sky-500/15 text-sky-200" : "border-zinc-800 bg-zinc-900 text-zinc-400 hover:border-zinc-700"}`}
                  >
                    Bragus Sameday direct <span className="block text-[10px] opacity-70">Contract direct · cu cost</span>
                  </button>
                </div>
                {isEmagOrder && awbSource === "bragus" && (
                  <p className="mt-1 text-[10px] text-amber-300">⚠️ Comandă eMAG generată cu AWB Bragus — vei plăti curieratul. Variantele eMAG sunt gratuite pe locker.</p>
                )}
              </div>

              <div>
                <label className="block text-[10px] font-medium uppercase tracking-wider text-zinc-500">Tip livrare</label>
                <div className="mt-1 flex gap-2">
                  <button data-testid="awb-type-home" onClick={() => setShippingType("home")}
                    className={`flex-1 rounded-md border px-3 py-2 text-sm font-medium transition ${shippingType === "home" ? "border-emerald-500/40 bg-emerald-500/10 text-emerald-200" : "border-zinc-800 bg-zinc-900 text-zinc-400 hover:border-zinc-700"}`}>
                    Domiciliu
                  </button>
                  <button data-testid="awb-type-locker" onClick={() => setShippingType("locker")}
                    className={`flex-1 rounded-md border px-3 py-2 text-sm font-medium transition ${shippingType === "locker" ? "border-violet-500/40 bg-violet-500/10 text-violet-200" : "border-zinc-800 bg-zinc-900 text-zinc-400 hover:border-zinc-700"}`}>
                    Locker (EasyBox)
                  </button>
                </div>
                {isLockerOrder && shippingType === "home" && (
                  <p className="mt-1 text-[10px] text-amber-300">⚠️ Comanda eMAG e setată ca locker — vrei să schimbi pe domiciliu?</p>
                )}
              </div>

              {shippingType === "locker" && (
                <div>
                  <label className="block text-[10px] font-medium uppercase tracking-wider text-zinc-500">Locker</label>
                  {lockerFromOrder && lockerId === lockerFromOrder ? (
                    <div className="mt-1 rounded-md border border-violet-500/40 bg-violet-500/10 px-3 py-2 text-sm text-violet-200">
                      Folosesc locker-ul din comandă: <span className="font-medium">{lockerNameFromOrder || "EasyBox"}</span>
                      <span className="ml-2 text-[11px] text-violet-300/80">(ID: <span className="font-mono">{lockerFromOrder}</span>)</span>
                      <button onClick={() => { setLockerId(""); searchLockers(); }} className="ml-2 text-[10px] text-violet-300 underline">schimbă</button>
                    </div>
                  ) : (
                    <>
                      <div className="mt-1 flex gap-2">
                        <div className="flex flex-1 items-center gap-1 rounded-md border border-zinc-800 bg-zinc-900 px-2 py-1.5">
                          <Search className="h-3.5 w-3.5 text-zinc-500" />
                          <input data-testid="awb-locker-search" value={lockerSearch} onChange={(e) => setLockerSearch(e.target.value)}
                            placeholder="Caută după oraș sau nume..."
                            className="flex-1 bg-transparent text-xs text-zinc-100 outline-none" />
                        </div>
                        <button onClick={searchLockers} className="rounded-md border border-zinc-800 bg-zinc-900 px-2 py-1.5 text-xs text-zinc-300 hover:border-zinc-700">Caută</button>
                      </div>
                      <div data-testid="awb-locker-list" className="mt-2 max-h-48 overflow-y-auto rounded-md border border-zinc-800 bg-zinc-900/40">
                        {lockerLoading && <div className="flex items-center gap-2 px-3 py-2 text-xs text-zinc-500"><Loader2 className="h-3 w-3 animate-spin" /> Se încarcă...</div>}
                        {!lockerLoading && lockers.length === 0 && <div className="px-3 py-2 text-xs text-zinc-600">Niciun locker găsit. Caută după oraș.</div>}
                        {lockers.map((l) => {
                          const id = l.lockerId || l.id;
                          return (
                            <button key={id} data-testid={`awb-locker-opt-${id}`} onClick={() => setLockerId(id)}
                              className={`flex w-full items-center justify-between gap-2 border-b border-zinc-800 px-3 py-2 text-left text-xs transition last:border-b-0 ${String(lockerId) === String(id) ? "bg-violet-500/10 text-violet-200" : "text-zinc-300 hover:bg-zinc-900"}`}>
                              <div className="min-w-0">
                                <p className="truncate font-medium">{l.name}</p>
                                <p className="text-[10px] text-zinc-500">{l.city} · {l.address}</p>
                              </div>
                              <span className="font-mono text-[10px] text-zinc-600">#{id}</span>
                            </button>
                          );
                        })}
                      </div>
                      {lockerId && <p className="mt-1 text-[10px] text-violet-300">Locker selectat: <span className="font-mono">{lockerId}</span></p>}
                    </>
                  )}
                </div>
              )}

              <div className="grid grid-cols-3 gap-2">
                <div>
                  <label className="block text-[10px] font-medium uppercase tracking-wider text-zinc-500">Greutate (kg)</label>
                  <input data-testid="awb-weight" type="number" step="0.1" min="0.1" value={weight} onChange={(e) => setWeight(e.target.value)}
                    className="mt-1 w-full rounded-md border border-zinc-800 bg-zinc-900 px-2 py-1.5 text-xs text-zinc-100 outline-none" />
                </div>
                <div>
                  <label className="block text-[10px] font-medium uppercase tracking-wider text-zinc-500">Nr. colete</label>
                  <input data-testid="awb-parcels" type="number" min="1" value={parcels} onChange={(e) => setParcels(e.target.value)}
                    className="mt-1 w-full rounded-md border border-zinc-800 bg-zinc-900 px-2 py-1.5 text-xs text-zinc-100 outline-none" />
                </div>
                <div>
                  <label className="block text-[10px] font-medium uppercase tracking-wider text-zinc-500">Asig. ({order.currency || "RON"})</label>
                  <input data-testid="awb-insured" type="number" min="0" value={insuredValue} onChange={(e) => setInsuredValue(e.target.value)}
                    className="mt-1 w-full rounded-md border border-zinc-800 bg-zinc-900 px-2 py-1.5 text-xs text-zinc-100 outline-none" />
                </div>
              </div>

              <div>
                <label className="block text-[10px] font-medium uppercase tracking-wider text-zinc-500">Observații (opțional, max 255 caractere)</label>
                <input data-testid="awb-observation" maxLength={255} value={observation} onChange={(e) => setObservation(e.target.value)}
                  placeholder="ex. fragil, nu lăsa pe scara blocului"
                  className="mt-1 w-full rounded-md border border-zinc-800 bg-zinc-900 px-2 py-1.5 text-xs text-zinc-100 outline-none" />
              </div>

              <div className="rounded-md border border-zinc-800 bg-zinc-900/40 p-3 text-[11px] text-zinc-300">
                <p className="mb-1 text-[10px] font-medium uppercase tracking-wider text-zinc-500">Destinatar</p>
                <p className="font-medium text-zinc-100">{c.name || "—"}</p>
                <p className="text-zinc-400">{c.phone_1 || c.phone || "—"}</p>
                <p className="mt-1 text-zinc-500">{c.shipping_street || c.billing_street}, {c.shipping_city || c.billing_city}, {country}</p>
                <div className="mt-2 grid grid-cols-2 gap-2 border-t border-zinc-800 pt-2">
                  <div>
                    <p className="text-[10px] text-zinc-500">Sursă AWB</p>
                    <p className={`font-semibold ${awbSource.startsWith("emag_fan") ? "text-violet-200" : awbSource.startsWith("emag_sameday_home") ? "text-emerald-200" : awbSource.startsWith("emag") ? "text-amber-200" : "text-sky-200"}`}>
                      {awbSource === "emag_sameday_locker" && "eMAG Sameday EasyBox (4EMG)"}
                      {awbSource === "emag_fan_locker" && "eMAG FAN Locker FANBox (7000)"}
                      {awbSource === "emag_sameday_home" && "eMAG Sameday Domiciliu"}
                      {awbSource === "bragus" && "Bragus Sameday direct"}
                      <span className="ml-1 text-[10px] text-zinc-500">{shippingType === "locker" ? "· Locker" : "· Domiciliu"}</span>
                    </p>
                  </div>
                  {codAmount > 0 && (
                    <div className="text-right">
                      <p className="text-[10px] text-zinc-500">Ramburs</p>
                      <p data-testid="awb-summary-cod" className="font-semibold text-amber-300">{codAmount.toFixed(2)} {order.currency || "RON"}</p>
                    </div>
                  )}
                </div>
              </div>
            </>
          )}

          {result && (
            <div data-testid="awb-result" className="space-y-2">
              <div className="flex items-start gap-2 rounded-lg border border-emerald-500/40 bg-emerald-500/10 px-3 py-2 text-sm text-emerald-200">
                <CheckCircle2 className="mt-0.5 h-4 w-4 flex-shrink-0" />
                <div>
                  <p className="font-semibold">AWB generat cu succes!</p>
                  <p className="mt-1 text-xs">Număr AWB: <span className="font-mono">{result.awb_number}</span></p>
                  <p className="text-xs">Upload eMAG: <span className="font-mono">{result.emag_push}</span></p>
                </div>
              </div>
              {result.url && (
                <a href={result.url.startsWith("/api/") ? `${BACKEND_URL}${result.url}` : result.url} target="_blank" rel="noreferrer"
                  className="inline-flex items-center gap-1.5 rounded-md border border-sky-500/40 bg-sky-500/10 px-3 py-2 text-xs font-medium text-sky-200 hover:bg-sky-500/20">
                  <ExternalLink className="h-3.5 w-3.5" /> Descarcă/Deschide AWB PDF
                </a>
              )}
            </div>
          )}
        </div>

        <div className="flex items-center justify-end gap-2 border-t border-zinc-800 bg-zinc-900/40 px-4 py-3">
          {result ? (
            <button onClick={onClose} className="rounded-md border border-zinc-700 bg-zinc-900 px-4 py-2 text-sm text-zinc-200 hover:border-zinc-600">Închide</button>
          ) : (
            <>
              <input
                ref={fileRef}
                type="file"
                accept="application/pdf"
                className="hidden"
                data-testid="awb-upload-input"
                onChange={(e) => handleUpload(e.target.files?.[0])}
              />
              <button
                data-testid="awb-upload-btn"
                onClick={() => fileRef.current?.click()}
                disabled={uploading || submitting}
                title="Încarcă un PDF AWB emis extern — se atașează ca un AWB generat de noi (push Shopify + fulfill, WhatsApp cu AWB, finalizare)"
                className="mr-auto inline-flex items-center gap-1.5 rounded-md border border-sky-500/40 bg-sky-500/10 px-3 py-2 text-xs font-medium text-sky-200 hover:bg-sky-500/20 disabled:opacity-50"
              >
                {uploading ? <><Loader2 className="h-3.5 w-3.5 animate-spin" /> Se încarcă...</> : <><Upload className="h-3.5 w-3.5" /> Upload PDF</>}
              </button>
              <button onClick={onClose} disabled={submitting || uploading} className="rounded-md border border-zinc-700 bg-zinc-900 px-4 py-2 text-sm text-zinc-300 hover:border-zinc-600 disabled:opacity-50">Anulează</button>
              <button data-testid="awb-submit" onClick={submit}
                disabled={submitting || uploading || (shippingType === "locker" && !lockerId) || (cod?.is_online_unpaid && !rambursChoice)}
                className="inline-flex items-center gap-1.5 rounded-md bg-orange-500 px-4 py-2 text-sm font-medium text-zinc-950 hover:bg-orange-400 disabled:opacity-50">
                {submitting ? <><Loader2 className="h-4 w-4 animate-spin" /> Se generează...</> : <>Generează AWB</>}
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
